import React from 'react';
import PropTypes from 'prop-types';
import { getIn } from 'formik';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';

import { FormikPropTypes, FormSchemaPropTypes } from './propTypes';

const preventDefault = event => event.preventDefault();

const Form = ({ formik, schema, formContext, openForEdit = false }) => {
  const getConditionalOptions = condition =>
    condition.when({ formik, formContext })
      ? cloneDeep(condition.then)
      : cloneDeep(condition.otherwise)
      || {};

  const evaluateFunctionOptions = options => {
    const evaluatedOptions = {
      additionalProps: typeof options.additionalProps === 'function'
        ? options.additionalProps(formContext, formik)
        : options.additionalProps,
    };

    return {
      ...options,
      ...evaluatedOptions,
    };
  };

  const inputs = schema.map(fieldOption => {
    const fieldWithEvaluatedOptions = evaluateFunctionOptions(fieldOption);

    const field = !fieldWithEvaluatedOptions.conditions ? fieldWithEvaluatedOptions
      : fieldWithEvaluatedOptions.conditions.reduce((options, condition) => {
        const newOptions = evaluateFunctionOptions(getConditionalOptions(condition));
        return merge(options, newOptions);
      }, cloneDeep(fieldWithEvaluatedOptions));

    const { additionalProps } = field;

    return React.createElement(field.component, {
      label: additionalProps && additionalProps.required
        ? <span>{field.meta.label}<span className="form-asterisk">&nbsp;*</span></span>
        : field.meta && field.meta.label,
      formik,
      name: field.name,
      value: field.meta && field.meta.valueGetter
        ? field.meta.valueGetter({ formik, formContext, name: field.name })
        : getIn(formik.values, field.name),
      disabled: field.options && field.options.disabledWhenEditing && openForEdit,
      key: field.name,
      options: field.meta && (field.meta.options || (formContext && field.meta.optionsKey
        ? getIn(formContext, field.meta.optionsKey)
        : null)),
      handleExternalChange: additionalProps && additionalProps.hidden,
      ...additionalProps,
    });
  });

  // On submit preventDefault required to prevent reload page, when only one input in form is present
  return (
    <form onSubmit={preventDefault}>
      {inputs}
    </form>
  );
};

Form.propTypes = {
  formik: FormikPropTypes.isRequired,
  schema: FormSchemaPropTypes.isRequired,
  openForEdit: PropTypes.bool,
  formContext: PropTypes.objectOf(PropTypes.any), // eslint-disable-line react/forbid-prop-types,
};

export default Form;
